import React, { useState, useEffect, useRef } from 'react';

const CodeEditor = () => {
  // File system and state
  const [files, setFiles] = useState([]);
  const [currentPath, setCurrentPath] = useState('/');
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileContent, setFileContent] = useState('');
  const [isFileExplorerVisible, setIsFileExplorerVisible] = useState(true);
  const [isAIAssistantVisible, setIsAIAssistantVisible] = useState(false);
  const [theme, setTheme] = useState('dark'); // 'dark' or 'light'
  const [activeTab, setActiveTab] = useState(null);
  const [openTabs, setOpenTabs] = useState([]);
  const editorRef = useRef(null);
  
  // Language selection state
  const [selectedLanguage, setSelectedLanguage] = useState('javascript');
  
  // AI assistant state
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiMessages, setAiMessages] = useState([
    { sender: 'assistant', content: 'Hello! I\'m Kiwi AI, your coding assistant in WebOS. How can I help you build something amazing today?' }
  ]);
  const [isAiThinking, setIsAiThinking] = useState(false);
  const [codeErrors, setCodeErrors] = useState([]);
  const [isValidating, setIsValidating] = useState(false);

  // Terminal state
  const [isTerminalVisible, setIsTerminalVisible] = useState(false);
  const [terminalOutput, setTerminalOutput] = useState([
    { type: 'info', content: 'WebOS Terminal v1.0 ready. Type "help" for commands.' }
  ]);
  const [terminalCommand, setTerminalCommand] = useState('');
  const [commandHistory, setCommandHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [isRunning, setIsRunning] = useState(false);
  const [runningFile, setRunningFile] = useState(null);
  const terminalInputRef = useRef(null);

  // App Builder state
  const [isAppBuilderVisible, setIsAppBuilderVisible] = useState(false);
  const [appMetadata, setAppMetadata] = useState({
    id: '',
    name: '',
    description: '',
    icon: 'puzzle-piece',
    category: 'productivity',
    author: 'WebOS User'
  });

  // Modal states
  const [isNewFileModalOpen, setIsNewFileModalOpen] = useState(false);
  const [isNewFolderModalOpen, setIsNewFolderModalOpen] = useState(false);
  const [newFileName, setNewFileName] = useState('new-file.js');
  const [newFolderName, setNewFolderName] = useState('new-folder');

  // Load files from localStorage on initial load
  useEffect(() => {
    loadFilesFromStorage();
  }, []);

  // Save file changes to localStorage
  useEffect(() => {
    if (selectedFile && fileContent !== undefined) {
      const updatedFiles = updateFileInTree(files, selectedFile.path, {
        ...selectedFile,
        content: fileContent,
        lastModified: new Date().toISOString()
      });
      saveFiles(updatedFiles);
    }
  }, [fileContent, selectedFile]);

  // Set language based on file extension when a file is selected
  useEffect(() => {
    if (selectedFile) {
      const fileType = selectedFile.type;
      setSelectedLanguage(fileType);
    }
  }, [selectedFile]);

  // Available language options
  const languageOptions = [
    { value: 'javascript', label: 'JavaScript', extension: 'js' },
    { value: 'typescript', label: 'TypeScript', extension: 'ts' },
    { value: 'html', label: 'HTML', extension: 'html' },
    { value: 'css', label: 'CSS', extension: 'css' },
    { value: 'json', label: 'JSON', extension: 'json' },
    { value: 'markdown', label: 'Markdown', extension: 'md' },
    { value: 'python', label: 'Python', extension: 'py' },
    { value: 'java', label: 'Java', extension: 'java' },
    { value: 'c', label: 'C', extension: 'c' },
    { value: 'cpp', label: 'C++', extension: 'cpp' },
    { value: 'csharp', label: 'C#', extension: 'cs' },
    { value: 'php', label: 'PHP', extension: 'php' },
    { value: 'ruby', label: 'Ruby', extension: 'rb' },
    { value: 'go', label: 'Go', extension: 'go' },
    { value: 'text', label: 'Plain Text', extension: 'txt' }
  ];

  // Load files from localStorage
  const loadFilesFromStorage = () => {
    try {
      const savedFiles = localStorage.getItem('webos_code_editor_files');
      if (savedFiles) {
        const parsedFiles = JSON.parse(savedFiles);
        setFiles(parsedFiles);
      } else {
        // Initialize with demo files
        const initialFiles = [
          {
            name: 'examples',
            type: 'folder',
            path: '/examples',
            children: [
              {
                name: 'hello-world.js',
                type: 'javascript',
                path: '/examples/hello-world.js',
                content: '// Hello World Example\n\nconsole.log("Hello, World!");\n\n// This is a basic JavaScript example\nfunction greet(name) {\n  return `Hello, ${name}!`;\n}\n\n// Call the function\nconst message = greet("Developer");\nconsole.log(message);',
                lastModified: new Date().toISOString()
              },
              {
                name: 'styles.css',
                type: 'css',
                path: '/examples/styles.css',
                content: '/* Basic CSS Styles */\n\nbody {\n  font-family: "Segoe UI", Arial, sans-serif;\n  background-color: #f5f5f5;\n  color: #333;\n  line-height: 1.6;\n  margin: 0;\n  padding: 20px;\n}\n\nh1 {\n  color: #2c3e50;\n  border-bottom: 2px solid #3498db;\n  padding-bottom: 10px;\n}\n\n.container {\n  max-width: 1200px;\n  margin: 0 auto;\n  padding: 20px;\n  background-color: white;\n  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);\n  border-radius: 5px;\n}',
                lastModified: new Date().toISOString()
              }
            ],
            lastModified: new Date().toISOString()
          },
          {
            name: 'README.md',
            type: 'markdown',
            path: '/README.md',
            content: '# WebOS Code Editor\n\nWelcome to the WebOS Code Editor! This is a simple code editor that runs in your browser.\n\n## Features\n\n- File explorer\n- Syntax highlighting\n- Multiple tabs\n- Dark/light theme switching\n\n## Getting Started\n\n1. Create new files or folders using the menu\n2. Click on a file to open it in the editor\n3. Edit and save files\n\nAll your files are stored in your browser\'s localStorage.',
            lastModified: new Date().toISOString()
          }
        ];
        setFiles(initialFiles);
        localStorage.setItem('webos_code_editor_files', JSON.stringify(initialFiles));
      }
    } catch (error) {
      console.error('Failed to load files from localStorage:', error);
    }
  };

  // Save files to localStorage
  const saveFiles = (updatedFiles) => {
    setFiles(updatedFiles);
    localStorage.setItem('webos_code_editor_files', JSON.stringify(updatedFiles));
  };

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Run code: F5 or Ctrl+Enter
      if ((e.key === 'F5' || (e.ctrlKey && e.key === 'Enter')) && selectedFile && !isRunning) {
        e.preventDefault();
        runCode();
      }
      
      // Toggle terminal: Ctrl+`
      if (e.ctrlKey && e.key === '`') {
        e.preventDefault();
        toggleTerminal();
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedFile, isRunning]);

  // Helper to update a file in the tree
  const updateFileInTree = (fileTree, filePath, updatedFile) => {
    return fileTree.map(file => {
      if (file.path === filePath) {
        return updatedFile;
      } else if (file.type === 'folder' && file.children) {
        return {
          ...file,
          children: updateFileInTree(file.children, filePath, updatedFile)
        };
      }
      return file;
    });
  };

  // Get current directory contents
  const getCurrentDirectoryContents = () => {
    if (currentPath === '/') {
      return files;
    }
    
    const pathParts = currentPath.split('/').filter(p => p);
    let currentLevel = files;
    
    for (const part of pathParts) {
      const folder = currentLevel.find(f => f.name === part && f.type === 'folder');
      if (folder && folder.children) {
        currentLevel = folder.children;
      } else {
        return [];
      }
    }
    
    return currentLevel;
  };

  // Handle file click in explorer
  const handleFileClick = (file) => {
    if (file.type === 'folder') {
      setCurrentPath(file.path);
    } else {
      setSelectedFile(file);
      setFileContent(file.content || '');
      
      // Add to open tabs if not already open
      if (!openTabs.find(tab => tab.path === file.path)) {
        setOpenTabs([...openTabs, file]);
      }
      
      setActiveTab(file.path);
    }
  };

  // Navigate up in directory
  const navigateUp = () => {
    if (currentPath === '/') return;
    
    const pathParts = currentPath.split('/').filter(p => p);
    pathParts.pop();
    const newPath = pathParts.length === 0 ? '/' : '/' + pathParts.join('/');
    setCurrentPath(newPath);
  };

  // Create a new file - opens modal instead of prompt
  const createNewFile = () => {
    setNewFileName('new-file.js');
    setIsNewFileModalOpen(true);
  };
  
  // Handle new file creation when form is submitted
  const handleNewFileSubmit = () => {
    if (!newFileName.trim()) {
      return;
    }
    
    const fileType = getFileType(newFileName);
    const newFilePath = currentPath === '/' ? `/${newFileName}` : `${currentPath}/${newFileName}`;
    
    const newFile = {
      name: newFileName,
      type: fileType,
      path: newFilePath,
      content: '',
      lastModified: new Date().toISOString()
    };
    
    // Add to current directory
    if (currentPath === '/') {
      const updatedFiles = [...files, newFile];
      saveFiles(updatedFiles);
    } else {
      const updatedFiles = addFileToDirectory(files, currentPath, newFile);
      saveFiles(updatedFiles);
    }
    
    // Automatically open the new file
    setSelectedFile(newFile);
    setFileContent(newFile.content || '');
    
    // Add to open tabs
    if (!openTabs.find(tab => tab.path === newFile.path)) {
      setOpenTabs([...openTabs, newFile]);
    }
    
    setActiveTab(newFile.path);
    setIsNewFileModalOpen(false);
  };

  // Create a new folder - opens modal instead of prompt
  const createNewFolder = () => {
    setNewFolderName('new-folder');
    setIsNewFolderModalOpen(true);
  };
  
  // Handle new folder creation when form is submitted
  const handleNewFolderSubmit = () => {
    if (!newFolderName.trim()) {
      return;
    }
    
    const newFolderPath = currentPath === '/' ? `/${newFolderName}` : `${currentPath}/${newFolderName}`;
    
    const newFolder = {
      name: newFolderName,
      type: 'folder',
      path: newFolderPath,
      children: [],
      lastModified: new Date().toISOString()
    };
    
    // Add to current directory
    if (currentPath === '/') {
      const updatedFiles = [...files, newFolder];
      saveFiles(updatedFiles);
    } else {
      const updatedFiles = addFileToDirectory(files, currentPath, newFolder);
      saveFiles(updatedFiles);
    }
    
    // Refresh files display to show the new folder
    const updatedCurrentDir = getCurrentDirectoryContents();
    
    // Display a success notification
    const notification = document.createElement('div');
    notification.style.position = 'fixed';
    notification.style.bottom = '20px';
    notification.style.right = '20px';
    notification.style.backgroundColor = 'rgba(76, 175, 80, 0.9)';
    notification.style.color = 'white';
    notification.style.padding = '12px 20px';
    notification.style.borderRadius = '6px';
    notification.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.2)';
    notification.style.zIndex = '9999';
    notification.style.fontSize = '14px';
    notification.style.display = 'flex';
    notification.style.alignItems = 'center';
    notification.style.gap = '10px';
    
    notification.innerHTML = `
      <i class="fas fa-check-circle" style="font-size: 18px;"></i>
      <div>
        <strong>Folder created!</strong>
        <div style="margin-top: 4px;">Successfully created folder: ${newFolderName}</div>
      </div>
    `;
    
    document.body.appendChild(notification);
    
    setTimeout(() => {
      if (document.body.contains(notification)) {
        document.body.removeChild(notification);
      }
    }, 3000);
    
    // Close the modal
    setIsNewFolderModalOpen(false);
  };

  // Custom modal component
  const Modal = ({ isOpen, onClose, title, children }) => {
    if (!isOpen) return null;
    
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}>
        <div style={{
          backgroundColor: theme === 'dark' ? 'rgba(40, 40, 60, 0.95)' : 'rgba(245, 245, 250, 0.95)',
          borderRadius: '8px',
          padding: '20px',
          width: '400px',
          maxWidth: '90%',
          boxShadow: '0 5px 15px rgba(0, 0, 0, 0.3)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '15px',
            borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
            paddingBottom: '10px',
          }}>
            <h3 style={{ 
              margin: 0, 
              color: theme === 'dark' ? '#fff' : '#333',
              fontSize: '16px',
              fontWeight: '500',
            }}>{title}</h3>
            <button 
              onClick={onClose}
              style={{
                background: 'transparent',
                border: 'none',
                color: theme === 'dark' ? '#aaa' : '#777',
                cursor: 'pointer',
                fontSize: '16px',
              }}
            >
              <i className="fas fa-times"></i>
            </button>
          </div>
          {children}
        </div>
      </div>
    );
  };

  // Add file to directory helper
  const addFileToDirectory = (fileTree, directoryPath, newFile) => {
    return fileTree.map(file => {
      if (file.type === 'folder' && file.path === directoryPath) {
        return {
          ...file,
          children: [...(file.children || []), newFile]
        };
      } else if (file.type === 'folder' && file.children) {
        return {
          ...file,
          children: addFileToDirectory(file.children, directoryPath, newFile)
        };
      }
      return file;
    });
  };

  // Delete selected file or folder
  const deleteSelected = () => {
    if (!selectedFile) return;
    
    if (window.confirm(`Are you sure you want to delete "${selectedFile.name}"?`)) {
      const updatedFiles = deleteFileFromTree(files, selectedFile.path);
      saveFiles(updatedFiles);
      
      // Remove from open tabs
      setOpenTabs(openTabs.filter(tab => tab.path !== selectedFile.path));
      
      if (activeTab === selectedFile.path) {
        const remainingTabs = openTabs.filter(tab => tab.path !== selectedFile.path);
        setActiveTab(remainingTabs.length > 0 ? remainingTabs[remainingTabs.length - 1].path : null);
        setSelectedFile(null);
        setFileContent('');
      }
    }
  };

  // Delete file helper
  const deleteFileFromTree = (fileTree, filePath) => {
    // For root level files
    const filteredTree = fileTree.filter(file => file.path !== filePath);
    if (filteredTree.length < fileTree.length) {
      return filteredTree;
    }
    
    // For nested files
    return fileTree.map(file => {
      if (file.type === 'folder' && file.children) {
        return {
          ...file,
          children: deleteFileFromTree(file.children, filePath)
        };
      }
      return file;
    });
  };

  // Handle tab click
  const handleTabClick = (tabPath) => {
    setActiveTab(tabPath);
    const file = findFileByPath(files, tabPath);
    if (file) {
      setSelectedFile(file);
      setFileContent(file.content || '');
    }
  };

  // Close tab
  const closeTab = (tabPath, event) => {
    if (event) {
      event.stopPropagation();
    }
    
    const newTabs = openTabs.filter(tab => tab.path !== tabPath);
    setOpenTabs(newTabs);
    
    if (activeTab === tabPath) {
      // Set the new active tab to the last one in the array
      if (newTabs.length > 0) {
        const newActiveTab = newTabs[newTabs.length - 1];
        setActiveTab(newActiveTab.path);
        setSelectedFile(newActiveTab);
        setFileContent(newActiveTab.content || '');
      } else {
        setActiveTab(null);
        setSelectedFile(null);
        setFileContent('');
      }
    }
  };

  // Helper to find a file by path
  const findFileByPath = (fileTree, path) => {
    for (const file of fileTree) {
      if (file.path === path) {
        return file;
      }
      if (file.type === 'folder' && file.children) {
        const found = findFileByPath(file.children, path);
        if (found) return found;
      }
    }
    return null;
  };

  // Helper to get file type from name
  const getFileType = (fileName) => {
    const extension = fileName.split('.').pop().toLowerCase();
    
    const typeMap = {
      'js': 'javascript',
      'jsx': 'javascript',
      'ts': 'typescript',
      'tsx': 'typescript',
      'html': 'html',
      'css': 'css',
      'json': 'json',
      'md': 'markdown',
      'txt': 'text',
      'py': 'python',
      'rb': 'ruby',
      'java': 'java',
      'c': 'c',
      'cpp': 'cpp',
      'cs': 'csharp',
      'go': 'go',
      'php': 'php'
    };
    
    return typeMap[extension] || 'text';
  };

  // Get icon for file
  const getFileIcon = (file) => {
    if (file.type === 'folder') {
      return 'folder';
    }
    
    const iconMap = {
      'javascript': 'js',
      'typescript': 'ts',
      'html': 'html',
      'css': 'css',
      'json': 'json',
      'markdown': 'markdown',
      'python': 'python',
      'ruby': 'gem',
      'java': 'java',
      'c': 'code',
      'cpp': 'code',
      'csharp': 'code',
      'go': 'code',
      'php': 'php'
    };
    
    return iconMap[file.type] || 'file-code';
  };

  // Render file explorer
  const renderFileExplorer = () => {
    if (!isFileExplorerVisible) return null;
    
    const currentContents = getCurrentDirectoryContents();
    
    return (
      <div className="file-explorer" style={{
        width: '250px',
        borderRight: '1px solid rgba(255, 255, 255, 0.1)',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: theme === 'dark' ? 'rgba(30, 30, 40, 0.6)' : 'rgba(240, 240, 245, 0.6)',
        color: theme === 'dark' ? '#fff' : '#333',
      }}>
        <div className="explorer-header" style={{
          padding: '8px',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          display: 'flex',
          justifyContent: 'space-between',
          backgroundColor: theme === 'dark' ? 'rgba(40, 40, 50, 0.6)' : 'rgba(230, 230, 235, 0.6)',
        }}>
          <div>EXPLORER</div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={createNewFile} title="New File" style={{
              background: 'transparent',
              border: 'none',
              color: theme === 'dark' ? '#fff' : '#333',
              cursor: 'pointer',
              padding: '2px',
            }}>
              <i className="fas fa-file-plus"></i>
            </button>
            <button onClick={createNewFolder} title="New Folder" style={{
              background: 'transparent',
              border: 'none',
              color: theme === 'dark' ? '#fff' : '#333',
              cursor: 'pointer',
              padding: '2px',
            }}>
              <i className="fas fa-folder-plus"></i>
            </button>
            <button onClick={refreshFileExplorer} title="Refresh" style={{
              background: 'transparent',
              border: 'none',
              color: theme === 'dark' ? '#fff' : '#333',
              cursor: 'pointer',
              padding: '2px',
            }}>
              <i className="fas fa-sync-alt"></i>
            </button>
          </div>
        </div>
        
        <div className="path-navigator" style={{
          padding: '8px',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
        }}>
          {currentPath !== '/' && (
            <button onClick={navigateUp} title="Navigate Up" style={{
              background: 'transparent',
              border: 'none',
              color: theme === 'dark' ? '#fff' : '#333',
              cursor: 'pointer',
              padding: '2px',
            }}>
              <i className="fas fa-arrow-up"></i>
            </button>
          )}
          <div style={{ flexGrow: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {currentPath}
          </div>
        </div>
        
        <div className="file-list" style={{
          overflowY: 'auto',
          flexGrow: 1,
          padding: '4px',
        }}>
          {currentContents.map((file) => (
            <div 
              key={file.path}
              className={`file-item ${selectedFile?.path === file.path ? 'selected' : ''}`}
              onClick={() => handleFileClick(file)}
              style={{
                padding: '6px 8px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                borderRadius: '4px',
                backgroundColor: selectedFile?.path === file.path 
                  ? (theme === 'dark' ? 'rgba(60, 120, 170, 0.3)' : 'rgba(60, 120, 170, 0.2)') 
                  : 'transparent',
                color: theme === 'dark' ? '#fff' : '#333',
              }}
            >
              <i className={`fas fa-${getFileIcon(file)}`} style={{
                color: file.type === 'folder' 
                  ? (theme === 'dark' ? '#e9c46a' : '#d4a83b') 
                  : (theme === 'dark' ? '#64b5f6' : '#1976d2')
              }}></i>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {file.name}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Handle content change
  const handleContentChange = (e) => {
    setFileContent(e.target.value);
    
    // Automatically check for errors after a brief delay
    if (selectedFile && selectedFile.type === 'javascript') {
      // Clear previous timeout if it exists
      if (window.codeValidationTimeout) {
        clearTimeout(window.codeValidationTimeout);
      }
      
      // Set a new timeout to validate code after typing stops
      window.codeValidationTimeout = setTimeout(() => {
        validateCode(e.target.value);
      }, 1000); // 1 second delay
    }
  };
  
  // Validate JavaScript code for errors
  const validateCode = (code) => {
    if (!code || !selectedFile) return;
    
    // Only validate JavaScript files for now
    if (selectedFile.type !== 'javascript') return;
    
    setIsValidating(true);
    
    try {
      // Basic syntax validation
      new Function(code);
      
      // Look for common issues
      const errors = [];
      
      // Check for missing semicolons
      const lines = code.split('\n');
      lines.forEach((line, index) => {
        const trimmedLine = line.trim();
        if (trimmedLine && 
            !trimmedLine.endsWith(';') && 
            !trimmedLine.endsWith('{') && 
            !trimmedLine.endsWith('}') && 
            !trimmedLine.endsWith('(') &&
            !trimmedLine.startsWith('//') &&
            !trimmedLine.startsWith('/*') &&
            !trimmedLine.startsWith('*') &&
            !trimmedLine.startsWith('import') &&
            !trimmedLine.startsWith('export') &&
            !trimmedLine.startsWith('function') &&
            !trimmedLine.startsWith('class') &&
            !trimmedLine.startsWith('if') &&
            !trimmedLine.startsWith('for') &&
            !trimmedLine.startsWith('while') &&
            !trimmedLine.endsWith(':')) {
          errors.push({
            line: index + 1,
            message: 'Missing semicolon',
            severity: 'warning',
            code: trimmedLine
          });
        }
      });
      
      // Check for undefined variables
      const variableDeclarations = code.match(/(?:var|let|const)\s+([a-zA-Z0-9_$]+)/g) || [];
      const declaredVars = variableDeclarations.map(v => v.replace(/(?:var|let|const)\s+/, ''));
      
      // Find all variable usages
      const variableUsages = code.match(/\b([a-zA-Z0-9_$]+)\b/g) || [];
      const jsKeywords = ['if', 'else', 'return', 'function', 'var', 'let', 'const', 'for', 'while', 'do', 'switch', 'case', 'break', 'continue', 'try', 'catch', 'finally', 'new', 'this', 'typeof', 'instanceof', 'true', 'false', 'null', 'undefined'];
      const jsBuiltins = ['console', 'document', 'window', 'Math', 'Object', 'Array', 'String', 'Number', 'Boolean', 'Date', 'Map', 'Set', 'JSON', 'Promise', 'setTimeout', 'setInterval', 'fetch', 'localStorage', 'sessionStorage'];
      
      // Find potentially undefined variables
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        const lineVarUsages = line.match(/\b([a-zA-Z0-9_$]+)\b/g) || [];
        
        for (const varName of lineVarUsages) {
          if (!declaredVars.includes(varName) && 
              !jsKeywords.includes(varName) && 
              !jsBuiltins.includes(varName) &&
              !varName.startsWith('_') && 
              varName.length > 1) {
            // Check if it looks like a function call
            if (line.includes(`${varName}(`)) {
              errors.push({
                line: i + 1,
                message: `Potential undefined function "${varName}"`,
                severity: 'error',
                code: line.trim()
              });
            } 
            // Skip small variables that might be parameters
            else if (varName.length > 2) {
              errors.push({
                line: i + 1,
                message: `Potential undefined variable "${varName}"`,
                severity: 'warning',
                code: line.trim()
              });
            }
          }
        }
      }
      
      // Check for missing React import in JSX files
      if (code.includes('<') && code.includes('/>') && !code.includes('import React')) {
        errors.push({
          line: 1,
          message: 'Missing React import in file with JSX',
          severity: 'error',
          code: 'File with JSX should import React'
        });
      }
      
      // Update the errors state
      setCodeErrors(errors);
      setIsValidating(false);
      
      // If there are errors and AI assistant is not visible, suggest opening it
      if (errors.length > 0 && !isAIAssistantVisible && errors.some(e => e.severity === 'error')) {
        const notification = document.createElement('div');
        notification.style.position = 'fixed';
        notification.style.bottom = '20px';
        notification.style.right = '20px';
        notification.style.backgroundColor = 'rgba(220, 53, 69, 0.9)';
        notification.style.color = 'white';
        notification.style.padding = '12px 20px';
        notification.style.borderRadius = '6px';
        notification.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.2)';
        notification.style.zIndex = '9999';
        notification.style.fontSize = '14px';
        notification.style.display = 'flex';
        notification.style.alignItems = 'center';
        notification.style.gap = '10px';
        
        notification.innerHTML = `
          <i class="fas fa-exclamation-circle" style="font-size: 18px;"></i>
          <div>
            <strong>Code errors detected!</strong>
            <div style="margin-top: 4px;">Click here to open AI Assistant for help.</div>
          </div>
        `;
        
        notification.style.cursor = 'pointer';
        notification.onclick = () => {
          setIsAIAssistantVisible(true);
          generateErrorFixSuggestion(errors);
          document.body.removeChild(notification);
        };
        
        document.body.appendChild(notification);
        
        setTimeout(() => {
          if (document.body.contains(notification)) {
            document.body.removeChild(notification);
          }
        }, 8000);
      }
      
      return errors;
    } catch (error) {
      // Syntax error
      const errorMatch = error.message.match(/line\s+(\d+)/i);
      const errorLine = errorMatch ? parseInt(errorMatch[1], 10) : null;
      
      const newErrors = [{
        line: errorLine || 1,
        message: `Syntax error: ${error.message}`,
        severity: 'error',
        code: errorLine ? code.split('\n')[errorLine - 1] : 'Syntax error in code'
      }];
      
      setCodeErrors(newErrors);
      setIsValidating(false);
      
      // Show notification for syntax error
      if (!isAIAssistantVisible) {
        const notification = document.createElement('div');
        notification.style.position = 'fixed';
        notification.style.bottom = '20px';
        notification.style.right = '20px';
        notification.style.backgroundColor = 'rgba(220, 53, 69, 0.9)';
        notification.style.color = 'white';
        notification.style.padding = '12px 20px';
        notification.style.borderRadius = '6px';
        notification.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.2)';
        notification.style.zIndex = '9999';
        notification.style.fontSize = '14px';
        notification.style.display = 'flex';
        notification.style.alignItems = 'center';
        notification.style.gap = '10px';
        
        notification.innerHTML = `
          <i class="fas fa-exclamation-triangle" style="font-size: 18px;"></i>
          <div>
            <strong>Syntax Error!</strong>
            <div style="margin-top: 4px;">Click here to get AI help fixing this error.</div>
          </div>
        `;
        
        notification.style.cursor = 'pointer';
        notification.onclick = () => {
          setIsAIAssistantVisible(true);
          generateErrorFixSuggestion(newErrors);
          document.body.removeChild(notification);
        };
        
        document.body.appendChild(notification);
        
        setTimeout(() => {
          if (document.body.contains(notification)) {
            document.body.removeChild(notification);
          }
        }, 8000);
      }
      
      return newErrors;
    }
  };
  
  // Generate AI suggestion for fixing errors
  const generateErrorFixSuggestion = (errors) => {
    if (!errors || errors.length === 0) return;
    
    setIsAiThinking(true);
    
    // Build a prompt based on the errors
    let errorPrompt = `I have the following errors in my ${selectedFile ? selectedFile.name : 'JavaScript code'}:\n\n`;
    
    errors.forEach((error, index) => {
      errorPrompt += `${index + 1}. ${error.severity.toUpperCase()} on line ${error.line}: ${error.message}\n`;
      errorPrompt += `   Code: ${error.code}\n\n`;
    });
    
    errorPrompt += "Can you help me fix these issues? Here's the relevant code:\n\n```javascript\n";
    errorPrompt += fileContent;
    errorPrompt += "\n```";
    
    // Add user message to chat
    const newMessages = [
      ...aiMessages,
      { sender: 'user', content: errorPrompt }
    ];
    setAiMessages(newMessages);
    
    // Groq API configuration (using Kiwi AI backend)
    const GROQ_API_KEY = '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';
    const GROQ_API_URL = 'https://api.groq.com/openai/v1/chat/completions';
    const MODEL = 'llama3-70b-8192';
    
    // Call Groq API
    const callKiwiAPI = async () => {
      try {
        // Format messages for API
        const messageHistory = aiMessages
          .filter(msg => msg.sender === 'user' || msg.sender === 'assistant')
          .map(msg => ({
            role: msg.sender === 'user' ? 'user' : 'assistant',
            content: msg.content
          }));
        
        // Add system instruction
        const messagesWithInstructions = [
          { 
            role: 'system', 
            content: `You are Kiwi AI, a coding assistant in WebOS. Always refer to yourself as "Kiwi AI".
You should help users fix errors in their code with clear explanations and examples.
When providing code examples, always use markdown code blocks with the appropriate language syntax highlighting.
Be concise but thorough in your explanations.`
          },
          ...messageHistory,
          { role: 'user', content: errorPrompt }
        ];
        
        const response = await fetch(GROQ_API_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${GROQ_API_KEY}`
          },
          body: JSON.stringify({
            model: MODEL,
            messages: messagesWithInstructions,
            temperature: 0.7,
            max_tokens: 1024
          })
        });
        
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(`API Error: ${errorData.error?.message || response.statusText}`);
        }
        
        const data = await response.json();
        const aiResponse = data.choices[0].message.content;
        
        // Add AI response to chat
        setAiMessages([...newMessages, { sender: 'assistant', content: aiResponse }]);
        setIsAiThinking(false);
      } catch (error) {
        console.error('Error calling API:', error);
        
        // Fallback to a simpler response if API fails
        setAiMessages([
          ...newMessages, 
          { 
            sender: 'assistant', 
            content: `I'm sorry, I couldn't analyze your code errors properly. Please try again later. Error: ${error.message}` 
          }
        ]);
        setIsAiThinking(false);
      }
    };
    
    // Call the API function
    callKiwiAPI();
  };

  // Refresh file explorer
  const refreshFileExplorer = () => {
    loadFilesFromStorage();
  };

  // Toggle file explorer visibility
  const toggleFileExplorer = () => {
    setIsFileExplorerVisible(!isFileExplorerVisible);
  };

  // Toggle theme
  const toggleTheme = () => {
    setTheme(theme === 'dark' ? 'light' : 'dark');
  };

  // Toggle terminal visibility
  const toggleTerminal = () => {
    setIsTerminalVisible(!isTerminalVisible);
  };
  
  const toggleAppBuilder = () => {
    setIsAppBuilderVisible(!isAppBuilderVisible);
    
    // Generate a unique ID based on current time if not already set
    if (!appMetadata.id) {
      const timestamp = Date.now().toString(36);
      const randomStr = Math.random().toString(36).substring(2, 8);
      setAppMetadata({
        ...appMetadata,
        id: `app-${timestamp}-${randomStr}`
      });
    }
  };

  // Execute terminal command
  const executeTerminalCommand = (command) => {
    // Add command to output
    setTerminalOutput(prev => [...prev, { type: 'command', content: `$ ${command}` }]);
    
    // Add to command history
    setCommandHistory(prev => [...prev, command]);
    setHistoryIndex(-1);
    
    // Process command
    const parts = command.trim().split(/\s+/);
    const cmd = parts[0].toLowerCase();
    const args = parts.slice(1);
    
    // Basic command simulation
    switch (cmd) {
      case 'help':
        setTerminalOutput(prev => [
          ...prev, 
          { 
            type: 'info', 
            content: 'Available commands:\n' +
                    '  help - Show this help message\n' +
                    '  clear - Clear terminal\n' +
                    '  ls - List files in current directory\n' +
                    '  cd <dir> - Change directory\n' +
                    '  pwd - Print working directory\n' +
                    '  cat <file> - Show file contents\n' +
                    '  echo <text> - Display text\n' +
                    '  run <file> - Run a JavaScript/HTML file\n' +
                    '  mkdir <name> - Create a directory\n' +
                    '  touch <name> - Create a file'
          }
        ]);
        break;
        
      case 'clear':
        setTerminalOutput([{ type: 'info', content: 'Terminal cleared.' }]);
        break;
        
      case 'ls':
        const contents = getCurrentDirectoryContents();
        const fileList = contents.map(file => 
          file.type === 'folder' 
            ? `📁 ${file.name}/` 
            : `📄 ${file.name}`
        ).join('\n');
        setTerminalOutput(prev => [
          ...prev, 
          { type: 'result', content: fileList || '(empty directory)' }
        ]);
        break;
        
      case 'cd':
        if (!args.length || args[0] === '~') {
          setCurrentPath('/');
          setTerminalOutput(prev => [...prev, { type: 'info', content: 'Changed to root directory' }]);
        } else if (args[0] === '..') {
          navigateUp();
          setTerminalOutput(prev => [...prev, { type: 'info', content: `Changed to ${currentPath}` }]);
        } else {
          const targetPath = args[0].startsWith('/') 
            ? args[0] 
            : currentPath === '/' 
              ? `/${args[0]}` 
              : `${currentPath}/${args[0]}`;
              
          const folder = findFileByPath(files, targetPath);
          
          if (folder && folder.type === 'folder') {
            setCurrentPath(folder.path);
            setTerminalOutput(prev => [...prev, { type: 'info', content: `Changed to ${folder.path}` }]);
          } else {
            setTerminalOutput(prev => [...prev, { type: 'error', content: `cd: ${args[0]}: No such directory` }]);
          }
        }
        break;
        
      case 'pwd':
        setTerminalOutput(prev => [...prev, { type: 'result', content: currentPath }]);
        break;
        
      case 'cat':
        if (!args.length) {
          setTerminalOutput(prev => [...prev, { type: 'error', content: 'cat: missing file operand' }]);
        } else {
          const targetPath = args[0].startsWith('/') 
            ? args[0] 
            : currentPath === '/' 
              ? `/${args[0]}` 
              : `${currentPath}/${args[0]}`;
              
          const file = findFileByPath(files, targetPath);
          
          if (file && file.type !== 'folder') {
            setTerminalOutput(prev => [...prev, { type: 'result', content: file.content || '(empty file)' }]);
          } else {
            setTerminalOutput(prev => [...prev, { type: 'error', content: `cat: ${args[0]}: No such file` }]);
          }
        }
        break;
        
      case 'echo':
        setTerminalOutput(prev => [...prev, { type: 'result', content: args.join(' ') }]);
        break;
        
      case 'run':
        if (!args.length) {
          setTerminalOutput(prev => [...prev, { type: 'error', content: 'run: missing file operand' }]);
        } else {
          const targetPath = args[0].startsWith('/') 
            ? args[0] 
            : currentPath === '/' 
              ? `/${args[0]}` 
              : `${currentPath}/${args[0]}`;
              
          const file = findFileByPath(files, targetPath);
          
          if (file && file.type !== 'folder') {
            // Set the file as selected and run it
            setSelectedFile(file);
            setFileContent(file.content || '');
            
            // Add to open tabs if not already open
            if (!openTabs.find(tab => tab.path === file.path)) {
              setOpenTabs(prev => [...prev, file]);
            }
            
            setActiveTab(file.path);
            
            // Execute the file
            setTerminalOutput(prev => [...prev, { type: 'info', content: `Running ${file.name}...` }]);
            runCode(file);
          } else {
            setTerminalOutput(prev => [...prev, { type: 'error', content: `run: ${args[0]}: No such file` }]);
          }
        }
        break;
        
      case 'mkdir':
        if (!args.length) {
          setTerminalOutput(prev => [...prev, { type: 'error', content: 'mkdir: missing directory operand' }]);
        } else {
          const newFolderName = args[0];
          const newFolderPath = currentPath === '/' ? `/${newFolderName}` : `${currentPath}/${newFolderName}`;
          
          const newFolder = {
            name: newFolderName,
            type: 'folder',
            path: newFolderPath,
            children: [],
            lastModified: new Date().toISOString()
          };
          
          // Add to current directory
          if (currentPath === '/') {
            const updatedFiles = [...files, newFolder];
            saveFiles(updatedFiles);
          } else {
            const updatedFiles = addFileToDirectory(files, currentPath, newFolder);
            saveFiles(updatedFiles);
          }
          
          setTerminalOutput(prev => [...prev, { type: 'success', content: `Directory created: ${newFolderName}` }]);
        }
        break;
        
      case 'touch':
        if (!args.length) {
          setTerminalOutput(prev => [...prev, { type: 'error', content: 'touch: missing file operand' }]);
        } else {
          const newFileName = args[0];
          const fileType = getFileType(newFileName);
          const newFilePath = currentPath === '/' ? `/${newFileName}` : `${currentPath}/${newFileName}`;
          
          const newFile = {
            name: newFileName,
            type: fileType,
            path: newFilePath,
            content: '',
            lastModified: new Date().toISOString()
          };
          
          // Add to current directory
          if (currentPath === '/') {
            const updatedFiles = [...files, newFile];
            saveFiles(updatedFiles);
          } else {
            const updatedFiles = addFileToDirectory(files, currentPath, newFile);
            saveFiles(updatedFiles);
          }
          
          setTerminalOutput(prev => [...prev, { type: 'success', content: `File created: ${newFileName}` }]);
        }
        break;
        
      default:
        setTerminalOutput(prev => [...prev, { type: 'error', content: `${cmd}: command not found` }]);
    }
    
    // Clear command input
    setTerminalCommand('');
  };
  
  // Handle terminal input
  const handleTerminalCommandSubmit = (e) => {
    e.preventDefault();
    if (!terminalCommand.trim()) return;
    
    executeTerminalCommand(terminalCommand.trim());
  };
  
  // Handle terminal key navigation (up/down arrows for history)
  const handleTerminalKeyDown = (e) => {
    // Up arrow - previous command in history
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (commandHistory.length > 0) {
        const newIndex = historyIndex < commandHistory.length - 1 ? historyIndex + 1 : historyIndex;
        setHistoryIndex(newIndex);
        setTerminalCommand(commandHistory[commandHistory.length - 1 - newIndex]);
      }
    }
    
    // Down arrow - next command in history
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (historyIndex > 0) {
        const newIndex = historyIndex - 1;
        setHistoryIndex(newIndex);
        setTerminalCommand(commandHistory[commandHistory.length - 1 - newIndex]);
      } else if (historyIndex === 0) {
        setHistoryIndex(-1);
        setTerminalCommand('');
      }
    }
  };

  // Run code function
  const runCode = (fileToRun) => {
    const targetFile = fileToRun || selectedFile;
    if (!targetFile || isRunning) return;
    
    setIsRunning(true);
    setRunningFile(targetFile);
    setIsTerminalVisible(true); // Show terminal when running code
    
    // Clear previous output
    setTerminalOutput([
      { type: 'info', content: `🚀 Running ${targetFile.name}...` }
    ]);
    
    // Simulate execution delay
    setTimeout(() => {
      try {
        const fileType = targetFile.type;
        const code = targetFile.content;
        
        // Handle different language executions
        if (fileType === 'javascript') {
          executeJavaScript(code);
        } else if (fileType === 'html') {
          executeHtml(code);
        } else if (fileType === 'python') {
          simulatePythonExecution(code);
        } else {
          // For other languages, just show a simulation message
          setTerminalOutput(prev => [
            ...prev,
            { type: 'info', content: `Simulating execution of ${fileType} code in WebOS environment.` },
            { type: 'success', content: `✅ Execution of ${targetFile.name} completed successfully.` }
          ]);
        }
      } catch (error) {
        setTerminalOutput(prev => [
          ...prev,
          { type: 'error', content: `❌ Error: ${error.message}` }
        ]);
      } finally {
        setIsRunning(false);
      }
    }, 500);
  };
  
  // Execute JavaScript code
  const executeJavaScript = (code) => {
    try {
      // Create a safe execution context
      const consoleMessages = [];
      
      // Create virtual console
      const virtualConsole = {
        log: (...args) => {
          const message = args.map(arg => 
            typeof arg === 'object' ? JSON.stringify(arg) : String(arg)
          ).join(' ');
          consoleMessages.push({ type: 'log', content: message });
        },
        error: (...args) => {
          const message = args.map(arg => 
            typeof arg === 'object' ? JSON.stringify(arg) : String(arg)
          ).join(' ');
          consoleMessages.push({ type: 'error', content: message });
        },
        warn: (...args) => {
          const message = args.map(arg => 
            typeof arg === 'object' ? JSON.stringify(arg) : String(arg)
          ).join(' ');
          consoleMessages.push({ type: 'warn', content: message });
        }
      };
      
      // Create a sandbox function to run the code
      const sandbox = new Function('console', code);
      
      // Execute the code with virtual console
      sandbox(virtualConsole);
      
      // Update terminal output with console messages
      setTerminalOutput(prev => [
        ...prev,
        ...consoleMessages,
        { type: 'success', content: '✅ Code executed successfully.' }
      ]);
    } catch (error) {
      setTerminalOutput(prev => [
        ...prev,
        { type: 'error', content: `❌ Runtime Error: ${error.message}` }
      ]);
    }
  };
  
  // Execute HTML code
  const executeHtml = (code) => {
    try {
      setTerminalOutput(prev => [
        ...prev,
        { 
          type: 'html', 
          content: 'HTML preview rendered. Check the preview panel.',
          htmlContent: code 
        },
        { type: 'success', content: '✅ HTML rendered successfully.' }
      ]);
    } catch (error) {
      setTerminalOutput(prev => [
        ...prev,
        { type: 'error', content: `❌ Error rendering HTML: ${error.message}` }
      ]);
    }
  };
  
  // Simulate Python execution (since we can't actually run Python in browser)
  const simulatePythonExecution = (code) => {
    const outputLines = [];
    
    // Check for print statements and simulate their output
    const printRegex = /print\s*\((.*)\)/g;
    let match;
    
    while ((match = printRegex.exec(code)) !== null) {
      try {
        let content = match[1].trim();
        // Handle string literals by removing quotes
        if ((content.startsWith('"') && content.endsWith('"')) || 
            (content.startsWith("'") && content.endsWith("'"))) {
          content = content.substring(1, content.length - 1);
        }
        outputLines.push({ type: 'log', content: content });
      } catch (e) {
        // If we can't parse it, just show the raw print statement
        outputLines.push({ type: 'log', content: `[Output from: ${match[0]}]` });
      }
    }
    
    if (outputLines.length === 0) {
      outputLines.push({ type: 'info', content: '[No print statements detected in Python code]' });
    }
    
    // Add the simulated output to terminal
    setTerminalOutput(prev => [
      ...prev,
      ...outputLines,
      { type: 'success', content: '✅ Python code simulation completed.' }
    ]);
  };

  // Handle language change
  const handleLanguageChange = (e) => {
    const newLanguage = e.target.value;
    setSelectedLanguage(newLanguage);
    
    if (selectedFile) {
      // Update file extension if needed
      const langOption = languageOptions.find(option => option.value === newLanguage);
      if (!langOption) return;
      
      // Check if we need to update the file name/extension
      const currentExt = selectedFile.name.split('.').pop();
      const selectedLangExt = langOption.extension;
      
      if (currentExt !== selectedLangExt) {
        const newName = selectedFile.name.split('.').slice(0, -1).join('.') + '.' + selectedLangExt;
        const newPath = selectedFile.path.split('/').slice(0, -1).join('/') + '/' + newName;
        
        // Update file object
        const updatedFile = {
          ...selectedFile,
          name: newName,
          path: newPath,
          type: newLanguage
        };
        
        // Update in files tree
        const updatedFiles = updateFileInTree(files, selectedFile.path, updatedFile);
        saveFiles(updatedFiles);
        
        // Update tabs
        const updatedTabs = openTabs.map(tab => {
          if (tab.path === selectedFile.path) {
            return updatedFile;
          }
          return tab;
        });
        setOpenTabs(updatedTabs);
        
        // Update selected file and active tab
        setSelectedFile(updatedFile);
        setActiveTab(updatedFile.path);
      } else {
        // Just update the type if extension is already correct
        const updatedFile = {
          ...selectedFile,
          type: newLanguage
        };
        
        const updatedFiles = updateFileInTree(files, selectedFile.path, updatedFile);
        saveFiles(updatedFiles);
        setSelectedFile(updatedFile);
      }
    }
  };

  // Handle AI assistant prompt submission
  const handleAiPromptSubmit = (e) => {
    e.preventDefault();
    if (!aiPrompt.trim()) return;
    
    // Add user message to chat
    const newMessages = [
      ...aiMessages,
      { sender: 'user', content: aiPrompt }
    ];
    setAiMessages(newMessages);
    
    // Clear input and show thinking state
    setAiPrompt('');
    setIsAiThinking(true);
    
    // Groq API configuration (using Kiwi AI backend)
    const GROQ_API_KEY = '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';
    const GROQ_API_URL = 'https://api.groq.com/openai/v1/chat/completions';
    const MODEL = 'llama3-70b-8192';
    
    // Call Groq API
    const callKiwiAPI = async () => {
      try {
        // Build prompt based on current context
        let prompt = aiPrompt;
        
        // Add code context if there's a selected file
        if (selectedFile && fileContent) {
          prompt = `I'm working on a ${selectedLanguage} file named ${selectedFile.name}. 
Here's my question: ${aiPrompt}

Current code:
\`\`\`${selectedLanguage}
${fileContent}
\`\`\`

Please provide helpful code examples and explanations related to my question.`;
        }
        
        // Format messages for API
        const messageHistory = aiMessages
          .filter(msg => msg.sender === 'user' || msg.sender === 'assistant')
          .map(msg => ({
            role: msg.sender === 'user' ? 'user' : 'assistant',
            content: msg.content
          }));
        
        // Add system instruction to ensure AI refers to itself as Kiwi AI
        const messagesWithInstructions = [
          { 
            role: 'system', 
            content: `You are Kiwi AI, a coding assistant in WebOS. Always refer to yourself as "Kiwi AI" and never mention any other model names or underlying technology.
You should provide helpful code examples and explain programming concepts clearly.
When providing code examples, always use markdown code blocks with the appropriate language syntax highlighting.
If the user is asking about the ${selectedLanguage} language, focus your answers on that language.`
          },
          ...messageHistory,
          { role: 'user', content: prompt }
        ];
        
        const response = await fetch(GROQ_API_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${GROQ_API_KEY}`
          },
          body: JSON.stringify({
            model: MODEL,
            messages: messagesWithInstructions,
            temperature: 0.7,
            max_tokens: 1024
          })
        });
        
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(`API Error: ${errorData.error?.message || response.statusText}`);
        }
        
        const data = await response.json();
        const aiResponse = data.choices[0].message.content;
        
        // Add AI response to chat
        setAiMessages([...newMessages, { sender: 'assistant', content: aiResponse }]);
        setIsAiThinking(false);
      } catch (error) {
        console.error('Error calling API:', error);
        
        // Fallback to a simpler response if API fails
        setAiMessages([
          ...newMessages, 
          { 
            sender: 'assistant', 
            content: `I'm sorry, I encountered an error while processing your request. Please try again later. Error: ${error.message}` 
          }
        ]);
        setIsAiThinking(false);
      }
    };
    
    // Call the API function
    callKiwiAPI();
  };

  // Insert code from AI assistant into editor
  const insertCodeIntoEditor = (code) => {
    if (!selectedFile || !code) return;
    
    // Set the new content (either replace all or insert at cursor position)
    setFileContent(code);
    
    // Flash feedback to show code was inserted
    const editorMain = document.querySelector('.editor-main');
    if (editorMain) {
      editorMain.style.transition = 'background-color 0.3s';
      editorMain.style.backgroundColor = theme === 'dark' ? 'rgba(40, 100, 150, 0.2)' : 'rgba(200, 230, 255, 0.3)';
      setTimeout(() => {
        editorMain.style.backgroundColor = '';
      }, 300);
    }
  };

  // Extract code from code blocks
  const extractCodeFromMarkdown = (markdown) => {
    if (!markdown.includes('```')) return null;
    
    const codeBlocks = [];
    const parts = markdown.split('```');
    
    for (let i = 1; i < parts.length; i += 2) {
      const block = parts[i];
      // Remove the language identifier line if exists
      const code = block.includes('\n') ? block.split('\n').slice(1).join('\n') : block;
      if (code.trim()) {
        codeBlocks.push(code);
      }
    }
    
    return codeBlocks.join('\n\n');
  };

  // Toggle AI assistant panel
  const toggleAiAssistant = () => {
    setIsAIAssistantVisible(!isAIAssistantVisible);
  };

  // Basic syntax highlighting
  const applySyntaxHighlighting = (code, fileType) => {
    if (!code) return '';

    // Only apply syntax highlighting for specific file types
    if (!['javascript', 'html', 'css', 'json'].includes(fileType)) {
      return code;
    }

    // Common syntax highlighting patterns
    let highlightedCode = code;

    // Function to escape HTML special characters
    const escapeHtml = (unsafe) => {
      return unsafe
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#039;");
    };

    // Escape HTML first
    highlightedCode = escapeHtml(highlightedCode);

    // JavaScript specific highlighting
    if (fileType === 'javascript') {
      // Keywords
      const keywords = ['var', 'let', 'const', 'function', 'return', 'if', 'else', 'for', 'while', 'do', 'switch', 'case', 'default', 'break', 'continue', 'try', 'catch', 'finally', 'throw', 'new', 'delete', 'typeof', 'instanceof', 'void', 'this', 'super', 'class', 'extends', 'static', 'get', 'set', 'import', 'export', 'from', 'as', 'async', 'await', 'yield', 'true', 'false', 'null', 'undefined'];
      
      // Apply keyword highlighting
      keywords.forEach(keyword => {
        const regex = new RegExp(`\\b${keyword}\\b`, 'g');
        highlightedCode = highlightedCode.replace(regex, `<span style="color: #c586c0;">${keyword}</span>`);
      });

      // String highlighting (both single and double quotes)
      highlightedCode = highlightedCode.replace(/(["'])(.*?)(\1)/g, '<span style="color: #ce9178;">$1$2$1</span>');
      
      // Number highlighting
      highlightedCode = highlightedCode.replace(/\b(\d+)\b/g, '<span style="color: #b5cea8;">$1</span>');
      
      // Comment highlighting (both single line and multi-line)
      highlightedCode = highlightedCode.replace(/\/\/(.*)$/gm, '<span style="color: #6a9955;">\/\/$1</span>');
      highlightedCode = highlightedCode.replace(/\/\*([\s\S]*?)\*\//g, '<span style="color: #6a9955;">\/\*$1\*\/</span>');
      
      // Function highlighting
      highlightedCode = highlightedCode.replace(/\b([a-zA-Z_$][a-zA-Z0-9_$]*)\s*\(/g, '<span style="color: #dcdcaa;">$1</span>(');
    }

    // Convert line breaks to <br> tags for proper rendering
    highlightedCode = highlightedCode.replace(/\n/g, '<br>');

    return highlightedCode;
  };

  // Render AI assistant panel
  const renderAiAssistant = () => {
    if (!isAIAssistantVisible) return null;
    
    return (
      <div className="ai-assistant-panel" style={{
        width: '300px',
        borderLeft: '1px solid rgba(255, 255, 255, 0.1)',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: theme === 'dark' ? 'rgba(30, 30, 40, 0.6)' : 'rgba(240, 240, 245, 0.6)',
        color: theme === 'dark' ? '#fff' : '#333',
        height: '100%',
      }}>
        <div className="assistant-header" style={{
          padding: '8px',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          backgroundColor: theme === 'dark' ? 'rgba(40, 40, 50, 0.6)' : 'rgba(230, 230, 235, 0.6)',
        }}>
          <div style={{ fontWeight: '500' }}>AI CODE ASSISTANT</div>
          <button 
            onClick={toggleAiAssistant} 
            title="Close Assistant"
            style={{
              background: 'transparent',
              border: 'none',
              color: theme === 'dark' ? '#fff' : '#333',
              cursor: 'pointer',
              padding: '2px',
            }}
          >
            <i className="fas fa-times"></i>
          </button>
        </div>
        
        <div className="chat-messages" style={{
          flexGrow: 1,
          overflowY: 'auto',
          padding: '10px',
          display: 'flex',
          flexDirection: 'column',
          gap: '10px',
        }}>
          {aiMessages.map((message, index) => (
            <div 
              key={index} 
              className={`chat-message ${message.sender}`}
              style={{
                alignSelf: message.sender === 'user' ? 'flex-end' : 'flex-start',
                maxWidth: '85%',
                padding: '8px 12px',
                borderRadius: '12px',
                backgroundColor: message.sender === 'user' 
                  ? (theme === 'dark' ? 'rgba(60, 120, 170, 0.3)' : 'rgba(60, 120, 170, 0.2)') 
                  : (theme === 'dark' ? 'rgba(70, 70, 90, 0.3)' : 'rgba(230, 230, 240, 0.5)'),
                wordBreak: 'break-word',
              }}
            >
              {message.sender === 'assistant' && (
                <div style={{ 
                  display: 'flex',
                  alignItems: 'center',
                  marginBottom: '5px',
                  fontSize: '12px',
                  color: theme === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)',
                }}>
                  <i className="fas fa-robot" style={{ marginRight: '5px' }}></i>
                  <span>AI Assistant</span>
                </div>
              )}
              <div style={{ 
                whiteSpace: 'pre-wrap',
              }}>
                {/* Render code blocks with syntax highlighting */}
                {message.content.includes('```') 
                  ? (
                    <>
                      {message.content.split('```').map((part, i) => {
                        if (i % 2 === 0) {
                          return <div key={i}>{part}</div>;
                        } else {
                          const code = part.includes('\n') ? part.split('\n').slice(1).join('\n') : part;
                          const lang = part.includes('\n') ? part.split('\n')[0] : '';
                          return (
                            <pre key={i} style={{
                              backgroundColor: theme === 'dark' ? 'rgba(20, 20, 30, 0.8)' : 'rgba(230, 230, 240, 0.8)',
                              padding: '10px',
                              borderRadius: '5px',
                              overflowX: 'auto',
                              fontFamily: 'Consolas, monospace',
                              fontSize: '12px',
                              lineHeight: '1.4',
                              marginTop: '5px',
                              marginBottom: '5px',
                            }}>
                              {code}
                            </pre>
                          );
                        }
                      })}
                      {message.sender === 'assistant' && selectedFile && (
                        <div style={{ 
                          marginTop: '10px', 
                          display: 'flex', 
                          justifyContent: 'flex-end' 
                        }}>
                          <button
                            onClick={() => {
                              const codeToInsert = extractCodeFromMarkdown(message.content);
                              if (codeToInsert) {
                                insertCodeIntoEditor(codeToInsert);
                              }
                            }}
                            style={{
                              backgroundColor: theme === 'dark' ? 'rgba(40, 120, 200, 0.6)' : 'rgba(40, 120, 200, 0.8)',
                              color: '#fff',
                              border: 'none',
                              borderRadius: '4px',
                              padding: '6px 12px',
                              fontSize: '12px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px',
                            }}
                          >
                            <i className="fas fa-file-import"></i>
                            Insert Code
                          </button>
                        </div>
                      )}
                    </>
                  )
                  : message.content
                }
              </div>
            </div>
          ))}
          {isAiThinking && (
            <div className="thinking-indicator" style={{
              alignSelf: 'flex-start',
              padding: '8px 12px',
              borderRadius: '12px',
              backgroundColor: theme === 'dark' ? 'rgba(70, 70, 90, 0.3)' : 'rgba(230, 230, 240, 0.5)',
              color: theme === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)',
              fontSize: '12px',
            }}>
              <i className="fas fa-circle-notch fa-spin" style={{ marginRight: '8px' }}></i>
              AI is thinking...
            </div>
          )}
        </div>
        
        <form 
          onSubmit={handleAiPromptSubmit}
          style={{
            borderTop: '1px solid rgba(255, 255, 255, 0.1)',
            padding: '10px',
            display: 'flex',
            gap: '10px',
            width: '100%',
            boxSizing: 'border-box',
            position: 'relative',
          }}
        >
          <input
            type="text"
            value={aiPrompt}
            onChange={(e) => setAiPrompt(e.target.value)}
            placeholder="Ask for code help..."
            style={{
              flexGrow: 1,
              padding: '8px 12px',
              paddingRight: '40px',
              borderRadius: '20px',
              border: theme === 'dark' ? '1px solid rgba(255, 255, 255, 0.2)' : '1px solid rgba(0, 0, 0, 0.2)',
              backgroundColor: theme === 'dark' ? 'rgba(25, 25, 35, 0.8)' : 'rgba(250, 250, 255, 0.8)',
              color: theme === 'dark' ? '#fff' : '#333',
              outline: 'none',
              width: 'calc(100% - 50px)',
              boxSizing: 'border-box',
            }}
          />
          <button
            type="submit"
            style={{
              position: 'absolute',
              right: '12px',
              top: '50%',
              transform: 'translateY(-50%)',
              backgroundColor: theme === 'dark' ? 'rgba(40, 120, 200, 0.6)' : 'rgba(40, 120, 200, 0.8)',
              color: '#fff',
              border: 'none',
              borderRadius: '50%',
              width: '32px',
              height: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
            }}
          >
            <i className="fas fa-paper-plane"></i>
          </button>
        </form>
      </div>
    );
  };

  // Handle app metadata changes
  const handleAppMetadataChange = (field, value) => {
    setAppMetadata({
      ...appMetadata,
      [field]: value
    });
  };

  // Handle publishing app to store
  const publishAppToStore = () => {
    if (!appMetadata.name.trim()) {
      alert('App name is required');
      return;
    }
    
    // Create app package with code files
    const appFiles = {}; // Object to store app files
    
    // Add selected file(s) to the app package
    if (selectedFile) {
      appFiles[selectedFile.name] = selectedFile.content || '';
    } else {
      alert('Please select a file to include in your app');
      return;
    }
    
    // Create the app data to store
    const appData = {
      ...appMetadata,
      files: appFiles,
      createdAt: new Date().toISOString(),
      component: 'CustomApp'
    };
    
    try {
      // Get existing custom apps
      const existingApps = JSON.parse(localStorage.getItem('webos-custom-apps') || '{}');
      
      // Add/update this app
      existingApps[appMetadata.id] = appData;
      
      // Save back to localStorage
      localStorage.setItem('webos-custom-apps', JSON.stringify(existingApps));
      
      // Trigger an event to notify the Store component
      const event = new CustomEvent('webos-store-app-published', { 
        detail: { appId: appMetadata.id }
      });
      window.dispatchEvent(event);
      
      alert('App successfully published to Store!');
    } catch (error) {
      console.error('Failed to publish app:', error);
      alert(`Failed to publish: ${error.message}`);
    }
  };

  // Render App Builder
  const renderAppBuilder = () => {
    return (
      <div style={{
        padding: '15px',
        height: '100%',
        overflow: 'auto',
        color: theme === 'dark' ? '#f0f0f0' : '#333',
        borderLeft: `1px solid ${theme === 'dark' ? '#333' : '#ddd'}`,
        backgroundColor: theme === 'dark' ? '#252525' : '#f5f5f5'
      }}>
        <h3 style={{ margin: '0 0 15px 0', borderBottom: `1px solid ${theme === 'dark' ? '#444' : '#ddd'}`, paddingBottom: '8px' }}>
          App Builder
        </h3>
        
        <div style={{ marginBottom: '15px' }}>
          <p style={{ fontSize: '14px', opacity: 0.8 }}>
            Create your custom app and publish it to the WebOS Store.
          </p>
        </div>
        
        <div style={{ marginBottom: '15px' }}>
          <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
            App Name*
          </label>
          <input
            type="text"
            value={appMetadata.name}
            onChange={(e) => handleAppMetadataChange('name', e.target.value)}
            style={{
              width: '100%',
              padding: '8px',
              backgroundColor: theme === 'dark' ? '#333' : '#fff',
              color: theme === 'dark' ? '#fff' : '#333',
              border: `1px solid ${theme === 'dark' ? '#555' : '#ccc'}`,
              borderRadius: '4px'
            }}
            placeholder="My Custom App"
          />
        </div>
        
        <div style={{ marginBottom: '15px' }}>
          <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
            Description*
          </label>
          <input
            type="text"
            value={appMetadata.description}
            onChange={(e) => handleAppMetadataChange('description', e.target.value)}
            style={{
              width: '100%',
              padding: '8px',
              backgroundColor: theme === 'dark' ? '#333' : '#fff',
              color: theme === 'dark' ? '#fff' : '#333',
              border: `1px solid ${theme === 'dark' ? '#555' : '#ccc'}`,
              borderRadius: '4px'
            }}
            placeholder="A brief description of your app"
          />
        </div>
        
        <div style={{ marginBottom: '15px' }}>
          <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
            Icon (Font Awesome)
          </label>
          <input
            type="text"
            value={appMetadata.icon}
            onChange={(e) => handleAppMetadataChange('icon', e.target.value)}
            style={{
              width: '100%',
              padding: '8px',
              backgroundColor: theme === 'dark' ? '#333' : '#fff',
              color: theme === 'dark' ? '#fff' : '#333',
              border: `1px solid ${theme === 'dark' ? '#555' : '#ccc'}`,
              borderRadius: '4px'
            }}
            placeholder="puzzle-piece, rocket, etc."
          />
          <div style={{ 
            display: 'flex', 
            gap: '10px', 
            flexWrap: 'wrap', 
            marginTop: '10px',
            justifyContent: 'center' 
          }}>
            {['puzzle-piece', 'rocket', 'cube', 'paint-brush', 'code', 'gamepad', 'music', 'calculator'].map(icon => (
              <div 
                key={icon} 
                onClick={() => handleAppMetadataChange('icon', icon)}
                style={{
                  width: '40px',
                  height: '40px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: appMetadata.icon === icon 
                    ? (theme === 'dark' ? '#4a90e2' : '#b2d7ff') 
                    : (theme === 'dark' ? '#333' : '#e9e9e9'),
                  borderRadius: '4px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
              >
                <i className={`fas fa-${icon}`}></i>
              </div>
            ))}
          </div>
        </div>
        
        <div style={{ marginBottom: '15px' }}>
          <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
            Category
          </label>
          <select
            value={appMetadata.category}
            onChange={(e) => handleAppMetadataChange('category', e.target.value)}
            style={{
              width: '100%',
              padding: '8px',
              backgroundColor: theme === 'dark' ? '#333' : '#fff',
              color: theme === 'dark' ? '#fff' : '#333',
              border: `1px solid ${theme === 'dark' ? '#555' : '#ccc'}`,
              borderRadius: '4px'
            }}
          >
            <option value="productivity">Productivity</option>
            <option value="tools">Tools</option>
            <option value="games">Games</option>
            <option value="media">Media</option>
            <option value="social">Social</option>
            <option value="utilities">Utilities</option>
          </select>
        </div>
        
        <div style={{ marginBottom: '15px' }}>
          <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
            Author
          </label>
          <input
            type="text"
            value={appMetadata.author}
            onChange={(e) => handleAppMetadataChange('author', e.target.value)}
            style={{
              width: '100%',
              padding: '8px',
              backgroundColor: theme === 'dark' ? '#333' : '#fff',
              color: theme === 'dark' ? '#fff' : '#333',
              border: `1px solid ${theme === 'dark' ? '#555' : '#ccc'}`,
              borderRadius: '4px'
            }}
            placeholder="Your name"
          />
        </div>
        
        <div style={{ marginBottom: '15px' }}>
          <p style={{ fontSize: '14px', marginBottom: '10px' }}>
            <strong>Selected File:</strong> {selectedFile ? selectedFile.name : 'None'}
          </p>
        </div>
        
        <button
          onClick={publishAppToStore}
          disabled={!appMetadata.name || !selectedFile}
          style={{
            width: '100%',
            padding: '10px',
            backgroundColor: appMetadata.name && selectedFile ? (theme === 'dark' ? '#4caf50' : '#2e7d32') : (theme === 'dark' ? '#555' : '#ccc'),
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: appMetadata.name && selectedFile ? 'pointer' : 'not-allowed',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px'
          }}
        >
          <i className="fas fa-upload"></i>
          Publish to Store
        </button>
      </div>
    );
  };

  return (
    <div className="code-editor" style={{
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      backgroundColor: theme === 'dark' ? 'rgba(30, 30, 40, 0.8)' : 'rgba(240, 240, 245, 0.8)',
      color: theme === 'dark' ? '#fff' : '#333',
    }}>
      <div className="editor-toolbar" style={{
        display: 'flex',
        padding: '8px',
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        backgroundColor: theme === 'dark' ? 'rgba(40, 40, 50, 0.8)' : 'rgba(230, 230, 235, 0.8)',
      }}>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button onClick={toggleFileExplorer} title={isFileExplorerVisible ? "Hide Explorer" : "Show Explorer"} style={{
            background: 'transparent',
            border: 'none',
            color: theme === 'dark' ? '#fff' : '#333',
            cursor: 'pointer',
            padding: '4px 8px',
          }}>
            <i className={`fas ${isFileExplorerVisible ? 'fa-chevron-left' : 'fa-chevron-right'}`}></i>
          </button>
          <button onClick={toggleTheme} title={`Switch to ${theme === 'dark' ? 'Light' : 'Dark'} Theme`} style={{
            background: 'transparent',
            border: 'none',
            color: theme === 'dark' ? '#fff' : '#333',
            cursor: 'pointer',
            padding: '4px 8px',
          }}>
            <i className={`fas ${theme === 'dark' ? 'fa-sun' : 'fa-moon'}`}></i>
          </button>
          <button onClick={runCode} disabled={!selectedFile || isRunning} title="Run Code (F5 or Ctrl+Enter)" style={{
            background: 'transparent',
            border: 'none',
            color: !selectedFile ? (theme === 'dark' ? '#555' : '#aaa') : (theme === 'dark' ? '#4CAF50' : '#2E7D32'),
            cursor: selectedFile ? 'pointer' : 'not-allowed',
            padding: '4px 8px',
          }}>
            <i className={`fas ${isRunning ? 'fa-spinner fa-spin' : 'fa-play'}`}></i>
          </button>
          <button onClick={toggleTerminal} title={`${isTerminalVisible ? "Hide" : "Show"} Terminal (Ctrl+\`)`} style={{
            background: 'transparent',
            border: 'none',
            color: theme === 'dark' ? '#fff' : '#333',
            cursor: 'pointer',
            padding: '4px 8px',
          }}>
            <i className="fas fa-terminal"></i>
          </button>
          <button 
            style={{
              background: 'none',
            border: 'none',
              color: theme === 'dark' ? '#999' : '#555',
            cursor: 'pointer',
              padding: '5px'
            }}
            onClick={toggleAiAssistant}
            title="Toggle AI Assistant"
          >
            <i className="fas fa-robot"></i>
          </button>
          
          <button 
            style={{
              background: 'none',
              border: 'none',
              color: isAppBuilderVisible 
                ? (theme === 'dark' ? '#4caf50' : '#2e7d32') 
                : (theme === 'dark' ? '#999' : '#555'),
              cursor: 'pointer',
              padding: '5px'
            }}
            onClick={toggleAppBuilder}
            title="Toggle App Builder"
          >
            <i className="fas fa-rocket"></i>
          </button>
        </div>
        
        {selectedFile && (
          <div style={{ 
            marginLeft: '15px', 
            display: 'flex', 
            alignItems: 'center',
            gap: '8px' 
          }}>
            <span style={{ fontSize: '12px', color: theme === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)' }}>
              Language:
            </span>
            <select 
              value={selectedLanguage}
              onChange={handleLanguageChange}
              style={{
                backgroundColor: theme === 'dark' ? 'rgba(60, 60, 70, 0.6)' : 'rgba(230, 230, 235, 0.8)',
                color: theme === 'dark' ? '#fff' : '#333',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '4px',
                padding: '2px 8px',
                fontSize: '13px',
                outline: 'none',
              }}
            >
              {languageOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        )}

        <div style={{ flexGrow: 1 }}></div>
        
        <div style={{ display: 'flex', gap: '8px' }}>
          {selectedFile && (
            <button onClick={deleteSelected} title="Delete File" style={{
              background: 'transparent',
              border: 'none',
              color: theme === 'dark' ? '#ff6b6b' : '#d32f2f',
              cursor: 'pointer',
              padding: '4px 8px',
            }}>
              <i className="fas fa-trash-alt"></i>
            </button>
          )}
        </div>
      </div>
      
      <div className="editor-tabs" style={{
        display: 'flex',
        overflowX: 'auto',
        backgroundColor: theme === 'dark' ? 'rgba(35, 35, 45, 0.8)' : 'rgba(235, 235, 240, 0.8)',
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
      }}>
        {openTabs.map(tab => (
          <div
            key={tab.path}
            className={`editor-tab ${activeTab === tab.path ? 'active' : ''}`}
            onClick={() => handleTabClick(tab.path)}
            style={{
              padding: '8px 12px',
              borderRight: '1px solid rgba(255, 255, 255, 0.1)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              backgroundColor: activeTab === tab.path 
                ? (theme === 'dark' ? 'rgba(50, 50, 60, 0.8)' : 'rgba(245, 245, 250, 0.8)') 
                : 'transparent',
              color: theme === 'dark' ? '#fff' : '#333',
              borderTop: activeTab === tab.path 
                ? '2px solid #64b5f6' 
                : '2px solid transparent',
            }}
          >
            <i className={`fas fa-${getFileIcon({ type: getFileType(tab.name) })}`} style={{
              color: theme === 'dark' ? '#64b5f6' : '#1976d2'
            }}></i>
            <span>{tab.name}</span>
            <button
              onClick={(e) => closeTab(tab.path, e)}
              style={{
                background: 'transparent',
                border: 'none',
                color: theme === 'dark' ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)',
                cursor: 'pointer',
                padding: '2px',
                marginLeft: '4px',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '16px',
                height: '16px',
              }}
            >
              <i className="fas fa-times" style={{ fontSize: '10px' }}></i>
            </button>
          </div>
        ))}
      </div>
      
      <div className="editor-content" style={{
        display: 'flex',
        flexGrow: 1,
        overflow: 'hidden',
      }}>
        {renderFileExplorer()}
        
        <div className="editor-main" style={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}>
          {selectedFile ? (
            <div style={{ position: 'relative', flexGrow: 1, display: 'flex' }}>
              {/* Line numbers */}
              <div
                style={{
                  width: '40px',
                  padding: '12px 5px',
                  backgroundColor: theme === 'dark' ? 'rgba(20, 20, 30, 0.8)' : 'rgba(240, 240, 245, 0.8)',
                  borderRight: '1px solid rgba(255, 255, 255, 0.1)',
                  textAlign: 'right',
                  fontFamily: 'Consolas, monospace',
                  fontSize: '14px',
                  lineHeight: '1.5',
                  color: theme === 'dark' ? 'rgba(255, 255, 255, 0.4)' : 'rgba(0, 0, 0, 0.4)',
                  userSelect: 'none',
                  overflow: 'hidden'
                }}
              >
                {fileContent.split('\n').map((_, i) => (
                  <div key={i} style={{ height: '1.5em' }}>{i + 1}</div>
                ))}
              </div>
              
              {/* Editor area */}
              <div style={{ position: 'relative', flexGrow: 1 }}>
                <textarea
                  ref={editorRef}
                  value={fileContent}
                  onChange={handleContentChange}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    padding: '12px',
                    border: 'none',
                    outline: 'none',
                    resize: 'none',
                    fontFamily: 'Consolas, monospace',
                    fontSize: '14px',
                    lineHeight: '1.5',
                    backgroundColor: theme === 'dark' ? 'rgba(25, 25, 35, 0.8)' : 'rgba(250, 250, 255, 0.8)',
                    color: 'transparent',
                    caretColor: theme === 'dark' ? '#f8f8f2' : '#333',
                    zIndex: 2,
                    whiteSpace: 'pre',
                    overflowWrap: 'normal',
                    overflowX: 'auto'
                  }}
                  spellCheck="false"
                />
                <div 
                  dangerouslySetInnerHTML={{ 
                    __html: applySyntaxHighlighting(fileContent, selectedFile.type) 
                  }}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    padding: '12px',
                    fontFamily: 'Consolas, monospace',
                    fontSize: '14px',
                    lineHeight: '1.5',
                    backgroundColor: theme === 'dark' ? 'rgba(25, 25, 35, 0.8)' : 'rgba(250, 250, 255, 0.8)',
                    color: theme === 'dark' ? '#f8f8f2' : '#333',
                    pointerEvents: 'none',
                    zIndex: 1,
                    whiteSpace: 'pre',
                    overflowWrap: 'normal',
                    overflowX: 'auto'
                  }}
                />
              </div>
            </div>
          ) : (
            <div className="welcome-screen" style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '20px',
              textAlign: 'center',
              height: '100%',
              backgroundColor: theme === 'dark' ? 'rgba(25, 25, 35, 0.8)' : 'rgba(250, 250, 255, 0.8)',
            }}>
              <i className="fas fa-code" style={{ fontSize: '48px', marginBottom: '20px', color: '#64b5f6' }}></i>
              <h2>WebOS Code Editor</h2>
              <p>Select a file from the explorer or create a new file to start coding.</p>
              <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                <button onClick={createNewFile} style={{
                  padding: '8px 16px',
                  backgroundColor: theme === 'dark' ? 'rgba(40, 120, 200, 0.6)' : 'rgba(40, 120, 200, 0.8)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}>
                  <i className="fas fa-file-plus"></i>
                  New File
                </button>
                <button onClick={createNewFolder} style={{
                  padding: '8px 16px',
                  backgroundColor: theme === 'dark' ? 'rgba(60, 60, 70, 0.6)' : 'rgba(180, 180, 190, 0.8)',
                  color: theme === 'dark' ? '#fff' : '#333',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}>
                  <i className="fas fa-folder-plus"></i>
                  New Folder
                </button>
              </div>
            </div>
          )}
          
          <div className="status-bar" style={{
            display: 'flex',
            justifyContent: 'space-between',
            padding: '4px 12px',
            fontSize: '12px',
            backgroundColor: theme === 'dark' ? 'rgba(45, 45, 55, 0.8)' : 'rgba(220, 220, 225, 0.8)',
            borderTop: '1px solid rgba(255, 255, 255, 0.1)',
            color: theme === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)',
          }}>
            <div>
              {selectedFile && (
                <>
                  <span style={{ marginRight: '15px' }}>{getFileType(selectedFile.name).toUpperCase()}</span>
                  <span>
                    Last modified: {new Date(selectedFile.lastModified).toLocaleString()}
                  </span>
                </>
              )}
            </div>
            <div>
              WebOS Code Editor
            </div>
          </div>
        </div>
        
        {/* AI Assistant */}
        {isAIAssistantVisible && (
          <div style={{
            width: '300px',
            height: '100%',
            borderLeft: `1px solid ${theme === 'dark' ? '#333' : '#ddd'}`,
            backgroundColor: theme === 'dark' ? '#252526' : '#f5f5f5',
            display: 'flex',
            flexDirection: 'column'
          }}>
        {renderAiAssistant()}
          </div>
        )}
        
        {/* App Builder */}
        {isAppBuilderVisible && (
          <div style={{
            width: '300px',
            height: '100%',
            borderLeft: `1px solid ${theme === 'dark' ? '#333' : '#ddd'}`,
            backgroundColor: theme === 'dark' ? '#252526' : '#f5f5f5',
            display: 'flex',
            flexDirection: 'column'
          }}>
            {renderAppBuilder()}
          </div>
        )}
      </div>
      
      {/* Terminal Panel */}
      {isTerminalVisible && (
        <div className="terminal-panel" style={{
          height: '200px',
          borderTop: '1px solid rgba(255, 255, 255, 0.1)',
          backgroundColor: theme === 'dark' ? 'rgba(20, 20, 30, 0.95)' : 'rgba(240, 240, 245, 0.95)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}>
          <div className="terminal-header" style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '6px 12px',
            backgroundColor: theme === 'dark' ? 'rgba(40, 40, 50, 0.8)' : 'rgba(220, 220, 225, 0.8)',
            borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '8px',
              color: theme === 'dark' ? '#f8f8f2' : '#333',
              fontWeight: '500',
              fontSize: '14px',
            }}>
              <i className="fas fa-terminal"></i>
              <span>Terminal</span>
              {isRunning && (
                <span style={{ 
                  fontSize: '12px',
                  backgroundColor: theme === 'dark' ? 'rgba(60, 60, 90, 0.5)' : 'rgba(230, 230, 240, 0.6)',
                  padding: '2px 8px',
                  borderRadius: '4px',
                  marginLeft: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px',
                }}>
                  <i className="fas fa-spinner fa-spin" style={{ fontSize: '10px' }}></i>
                  Running {runningFile?.name}...
                </span>
              )}
              {!isRunning && runningFile && terminalOutput.length > 0 && terminalOutput[terminalOutput.length - 1].type === 'success' && (
                <span style={{ 
                  fontSize: '12px',
                  backgroundColor: theme === 'dark' ? 'rgba(40, 100, 40, 0.3)' : 'rgba(200, 255, 200, 0.4)',
                  color: theme === 'dark' ? '#4CAF50' : '#2E7D32',
                  padding: '2px 8px',
                  borderRadius: '4px',
                  marginLeft: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px',
                }}>
                  <i className="fas fa-check-circle" style={{ fontSize: '10px' }}></i>
                  {runningFile.name} completed successfully
                </span>
              )}
              {!isRunning && runningFile && terminalOutput.length > 0 && terminalOutput[terminalOutput.length - 1].type === 'error' && (
                <span style={{ 
                  fontSize: '12px',
                  backgroundColor: theme === 'dark' ? 'rgba(100, 40, 40, 0.3)' : 'rgba(255, 200, 200, 0.4)',
                  color: theme === 'dark' ? '#ff6b6b' : '#d32f2f',
                  padding: '2px 8px',
                  borderRadius: '4px',
                  marginLeft: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px',
                }}>
                  <i className="fas fa-times-circle" style={{ fontSize: '10px' }}></i>
                  {runningFile.name} failed with errors
                </span>
              )}
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button 
                onClick={() => setTerminalOutput([{ type: 'info', content: 'Terminal cleared.' }])} 
                title="Clear Terminal"
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: theme === 'dark' ? '#ddd' : '#555',
                  cursor: 'pointer',
                  padding: '2px',
                }}
              >
                <i className="fas fa-trash-alt"></i>
              </button>
              <button 
                onClick={toggleTerminal} 
                title="Close Terminal"
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: theme === 'dark' ? '#ddd' : '#555',
                  cursor: 'pointer',
                  padding: '2px',
                }}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
          </div>
          
          <div 
            className="terminal-content" 
            style={{
            flexGrow: 1,
            overflowY: 'auto',
            padding: '10px',
            fontFamily: 'Consolas, monospace',
            fontSize: '13px',
            color: theme === 'dark' ? '#f8f8f2' : '#333',
            }}
          >
            {terminalOutput.map((line, index) => (
              <div 
                key={index} 
                style={{
                  marginBottom: '4px',
                  color: line.type === 'error' 
                    ? '#ff6b6b' 
                    : line.type === 'warn' 
                    ? '#ffd700' 
                    : line.type === 'success' 
                    ? '#4CAF50' 
                    : line.type === 'info' 
                    ? (theme === 'dark' ? '#64b5f6' : '#1976d2')
                    : line.type === 'command'
                    ? (theme === 'dark' ? '#e2e2e2' : '#2c2c2c')
                    : (theme === 'dark' ? '#f8f8f2' : '#333'),
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '6px',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
              >
                {line.type === 'error' && <i className="fas fa-times-circle" style={{ marginTop: '2px' }}></i>}
                {line.type === 'warn' && <i className="fas fa-exclamation-triangle" style={{ marginTop: '2px' }}></i>}
                {line.type === 'success' && <i className="fas fa-check-circle" style={{ marginTop: '2px' }}></i>}
                {line.type === 'info' && <i className="fas fa-info-circle" style={{ marginTop: '2px' }}></i>}
                {line.type === 'log' && <i className="fas fa-angle-right" style={{ marginTop: '2px' }}></i>}
                {line.type === 'command' && <i className="fas fa-terminal" style={{ marginTop: '2px' }}></i>}
                
                {line.type === 'html' ? (
                  <div style={{ flexGrow: 1 }}>
                    <div>{line.content}</div>
                    <div style={{
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      borderRadius: '4px',
                      padding: '10px',
                      marginTop: '5px',
                      backgroundColor: theme === 'dark' ? 'rgba(40, 40, 50, 0.5)' : 'rgba(250, 250, 255, 0.5)',
                    }}>
                      <iframe
                        title="HTML Preview"
                        srcDoc={line.htmlContent}
                        style={{
                          width: '100%',
                          height: '150px',
                          border: 'none',
                          backgroundColor: '#fff',
                        }}
                        sandbox="allow-scripts"
                      />
                    </div>
                  </div>
                ) : (
                  <div style={{ flexGrow: 1 }}>{line.content}</div>
                )}
              </div>
            ))}
          </div>
          
          <form 
            onSubmit={handleTerminalCommandSubmit}
            style={{
              display: 'flex',
              borderTop: '1px solid rgba(255, 255, 255, 0.1)',
              backgroundColor: theme === 'dark' ? 'rgba(30, 30, 40, 0.8)' : 'rgba(235, 235, 240, 0.8)',
              padding: '8px',
            }}
          >
            <div style={{
              color: theme === 'dark' ? '#64b5f6' : '#1976d2',
              fontFamily: 'Consolas, monospace',
              fontSize: '13px',
              paddingRight: '8px',
            }}>$</div>
            <input
              ref={terminalInputRef}
              type="text"
              value={terminalCommand}
              onChange={(e) => setTerminalCommand(e.target.value)}
              onKeyDown={handleTerminalKeyDown}
              style={{
                flex: 1,
                backgroundColor: 'transparent',
                border: 'none',
                outline: 'none',
                color: theme === 'dark' ? '#f8f8f2' : '#333',
                fontFamily: 'Consolas, monospace',
                fontSize: '13px',
              }}
              placeholder="Type command here..."
              spellCheck="false"
              autoComplete="off"
            />
          </form>
        </div>
      )}
      
      {/* New File Modal */}
      <Modal 
        isOpen={isNewFileModalOpen} 
        onClose={() => setIsNewFileModalOpen(false)} 
        title="Create New File"
      >
        <form onSubmit={(e) => { e.preventDefault(); handleNewFileSubmit(); }}>
          <div style={{ marginBottom: '15px' }}>
            <label 
              htmlFor="fileName" 
              style={{ 
                display: 'block', 
                marginBottom: '5px',
                color: theme === 'dark' ? '#ddd' : '#555',
                fontSize: '14px',
              }}
            >
              File Name:
            </label>
            <input
              id="fileName"
              type="text"
              value={newFileName}
              onChange={(e) => setNewFileName(e.target.value)}
              autoFocus
              style={{
                width: '100%',
                padding: '8px 12px',
                borderRadius: '4px',
                border: theme === 'dark' ? '1px solid rgba(255, 255, 255, 0.2)' : '1px solid rgba(0, 0, 0, 0.2)',
                backgroundColor: theme === 'dark' ? 'rgba(30, 30, 50, 0.7)' : 'rgba(255, 255, 255, 0.9)',
                color: theme === 'dark' ? '#fff' : '#333',
                fontSize: '14px',
                outline: 'none',
              }}
            />
            <p style={{ 
              margin: '5px 0 0 0', 
              fontSize: '12px', 
              color: theme === 'dark' ? '#aaa' : '#777',
            }}>
              Extension determines the file type (.js, .html, .css, etc.)
            </p>
          </div>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'flex-end',
            gap: '10px',
          }}>
            <button
              type="button"
              onClick={() => setIsNewFileModalOpen(false)}
              style={{
                padding: '8px 16px',
                border: 'none',
                borderRadius: '4px',
                backgroundColor: theme === 'dark' ? 'rgba(60, 60, 80, 0.6)' : 'rgba(200, 200, 210, 0.6)',
                color: theme === 'dark' ? '#ddd' : '#333',
                cursor: 'pointer',
                fontSize: '14px',
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              style={{
                padding: '8px 16px',
                border: 'none',
                borderRadius: '4px',
                backgroundColor: theme === 'dark' ? 'rgba(40, 120, 200, 0.6)' : 'rgba(40, 120, 200, 0.8)',
                color: '#fff',
                cursor: 'pointer',
                fontSize: '14px',
              }}
            >
              Create
            </button>
          </div>
        </form>
      </Modal>
      
      {/* New Folder Modal */}
      <Modal 
        isOpen={isNewFolderModalOpen} 
        onClose={() => setIsNewFolderModalOpen(false)} 
        title="Create New Folder"
      >
        <form onSubmit={(e) => { e.preventDefault(); handleNewFolderSubmit(); }}>
          <div style={{ marginBottom: '15px' }}>
            <label 
              htmlFor="folderName" 
              style={{ 
                display: 'block', 
                marginBottom: '5px',
                color: theme === 'dark' ? '#ddd' : '#555',
                fontSize: '14px',
              }}
            >
              Folder Name:
            </label>
            <input
              id="folderName"
              type="text"
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              autoFocus
              style={{
                width: '100%',
                padding: '8px 12px',
                borderRadius: '4px',
                border: theme === 'dark' ? '1px solid rgba(255, 255, 255, 0.2)' : '1px solid rgba(0, 0, 0, 0.2)',
                backgroundColor: theme === 'dark' ? 'rgba(30, 30, 50, 0.7)' : 'rgba(255, 255, 255, 0.9)',
                color: theme === 'dark' ? '#fff' : '#333',
                fontSize: '14px',
                outline: 'none',
              }}
            />
          </div>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'flex-end',
            gap: '10px',
          }}>
            <button
              type="button"
              onClick={() => setIsNewFolderModalOpen(false)}
              style={{
                padding: '8px 16px',
                border: 'none',
                borderRadius: '4px',
                backgroundColor: theme === 'dark' ? 'rgba(60, 60, 80, 0.6)' : 'rgba(200, 200, 210, 0.6)',
                color: theme === 'dark' ? '#ddd' : '#333',
                cursor: 'pointer',
                fontSize: '14px',
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              style={{
                padding: '8px 16px',
                border: 'none',
                borderRadius: '4px',
                backgroundColor: theme === 'dark' ? 'rgba(40, 120, 200, 0.6)' : 'rgba(40, 120, 200, 0.8)',
                color: '#fff',
                cursor: 'pointer',
                fontSize: '14px',
              }}
            >
              Create
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default CodeEditor; 